import { CartItem } from '@/models/cart/cart-item'
import { isDisplayItem } from '@/models/cart/group/group'
import { AppliedPoint, DiscountAmount, DiscountPrice } from '@/models/discount-amount/discount-amount'
import CalculateBundleDiscount from '@/models/estimate/usecase/bundle-discount/calculate-bundle-discount'
import { BaseError } from '@/util/base-error'
import { FrontPriceSection, PurchaseEstimate } from '@spa/services/api/purchase'
import { kind as OrderKind, Order } from '@spa/services/order'
import { filterValidItemAsAggregate, isSinglePurchasable } from '@spa/store/modules/cart/function'
import { Either, left, right } from 'fp-ts/lib/Either'
import { fromNullable, Option } from 'fp-ts/lib/Option'
import { get } from 'lodash'

/**
 * 1注文画面で複数の購入手続きが行われることがあるため、それらを管理するための状態オブジェクト
 *
 * 注文 order というと注文を行おうとする文脈、過去の注文を閲覧する文脈双方で捉えられてしまうので、
 * 購入手続きという意味を意識して checkout という単語を採用してみている。
 * （他のモジュールでは checkout という単語は使われていないので補足説明）
 *
 * 基本的には
 * - カートアイテムを適切な知識で分割した Order[]
 * - Orderごとに実行された見積もり結果 PurchaseEstimate[]
 * で算出されるが、ユーザーが入力したポイントによる割引額も管理する必要があるのでstoreの格好をしている
 *
 * IMPORTANT
 * - TODOとして語彙の整理は順次行う
 *   - order, purchase, payment, checkoutあたり
 *   - checkoutという語の利用を継続するとして、既存のorderという単位は廃止できるかもしれない
 * - 上記の整理が進行中は、ひとまずcheckoutという語彙をモジュールの外にまで広げないようにする
 *
 * @see https://github.com/Everforth/growthpalette-front/pull/8269#discussion_r1907640713
 */
export class CheckoutStore {
  static empty(): CheckoutStore {
    return new CheckoutStore([])
  }

  /**
   * ordersとestimatesは同じ順序で並んでいなければならない！
   */
  static create(
    orders: Order[],
    estimates: PurchaseEstimate[],
    pointDiscounted: number // 適用ポイント数ではなく割引額（円）
  ): Either<Error, CheckoutStore> {
    if (orders.length !== estimates.length) {
      return left(new MismatchLengthError('the length of orders and estimates must be the same'))
    }
    const pointDiscountedAllocation = allAmountToFirstAllocationRule(orders.length, pointDiscounted)

    return right(new CheckoutStore(
      orders.map((order, i) => Checkout.create(
        order,
        estimates[i],
        pointDiscountedAllocation[i]
      ))
    ))
  }

  private constructor(
    public readonly checkoutList: Checkout[]
  ) {}

  /**
   * checkoutKeyで指定されたCheckoutのみを持つCheckoutStoreを返却する
   */
  convertToExtractedStore(checkoutKey: CheckoutKey): CheckoutStore {
    return this.findByKey(checkoutKey).fold(
      CheckoutStore.empty(),
      checkout => new CheckoutStore([checkout])
    )
  }

  get hasMultipleCheckouts(): boolean {
    return this.checkoutList.length > 1
  }

  get itemCount(): number {
    return this.sum(
      this.checkoutList.map(c => c.totalItemCount)
    )
  }

  get noveltyCount(): number {
    return this.sum(
      this.checkoutList.map(c => c.noveltyCount)
    )
  }

  get totalPrice(): number {
    return this.sum(this.totalPriceList)
  }

  get totalPriceList(): number[] {
    return this.checkoutList.map(c => c.totalPrice).filter(price => price > 0)
  }

  get subtotalPrice(): number {
    return this.sum(this.subtotalPriceList)
  }

  get subtotalPriceList(): number[] {
    return this.checkoutList.map(c => c.subtotalPrice).filter(price => price > 0)
  }

  get commission(): number {
    return this.sum(this.commissionList)
  }

  get commissionList(): number[] {
    return this.checkoutList.map(c => c.commission).filter(price => price > 0)
  }

  get shipping(): number {
    return this.sum(this.shippingList)
  }

  get shippingList(): number[] {
    return this.checkoutList.map(c => c.shippingFee).filter(price => price > 0)
  }

  get serviceFee(): number {
    return this.sum(this.serviceFeeList)
  }

  get serviceFeeList(): number[] {
    return this.checkoutList.map(c => c.serviceFee).filter(price => price > 0)
  }

  get totalDiscountPrice(): number {
    return this.checkoutList.reduce(
      (total, checkout) => total + checkout.estimatedDiscountPrice,
      0
    )
  }

  get bundleDiscountPrice(): number {
    return new CalculateBundleDiscount(
      this.checkoutList.map(c => c.estimatedPriceSection)
    ).apply()
  }

  get couponDiscountPrice(): number {
    return this.sum(this.couponDiscountList)
  }

  get couponDiscountList(): number[] {
    return this.checkoutList.map(c => c.couponDiscountPrice).filter(price => price > 0)
  }

  get pointDiscountPrice(): number {
    return this.sum(this.pointDiscountList)
  }

  get pointDiscountList(): number[] {
    return this.checkoutList.map(c => c.pointDiscounted).filter(price => price > 0)
  }

  get estimatedPoint(): number {
    return this.sum(this.estimatedPointList)
  }

  get estimatedPointList(): number[] {
    return this.checkoutList.map(c => c.estimatedPoint).filter(price => price > 0)
  }

  get allCheckoutKeys(): CheckoutKey[] {
    return this.checkoutList.map(c => c.key)
  }

  findByKey(checkoutKey: CheckoutKey): Option<Checkout> {
    return fromNullable(
      this.checkoutList.find(checkout => checkout.key.equalsTo(checkoutKey))
    )
  }

  private sum(prices: number[]): number {
    return prices.reduce((total, price) => total + price, 0)
  }
}

/**
 * 値をある長さの配列に分配する
 */
type ValueAllocationPolicy<T> = (length: number, value: T) => T[]
/**
 * すべてを先頭に割り当てる
 */
const allAmountToFirstAllocationRule: ValueAllocationPolicy<number> =
  (length: number, value: number) => [value, ...Array.from({ length: length - 1 }, () => 0)]

/**
 * 購入に必要な情報をまとめる
 */
export class Checkout {
  static create(
    order: Order,
    estimate: PurchaseEstimate,
    pointDiscounted: number
  ): Checkout {
    return new Checkout(
      CheckoutKey.create(order),
      order,
      estimate,
      pointDiscounted
    )
  }

  private constructor(
    public readonly key: CheckoutKey,
    public readonly order: Order,
    public readonly estimate: PurchaseEstimate,
    public readonly pointDiscounted: number
  ) {}

  get cartItems(): CartItem[] {
    return this.order.cartItems
  }

  get displayItems(): CartItem[] {
    return this.filterDisplayItems(this.cartItems)
  }

  get noveltyItems(): CartItem[] {
    return this.cartItems.filter(item => item.isNovelty)
  }

  get totalItemCount(): number {
    return this.displayItems.reduce(
      (count, cartItem) => count + cartItem.unitCount,
      0
    )
  }

  get noveltyCount(): number {
    return this.noveltyItems.reduce(
      (count, noveltyItem) => count + noveltyItem.unitCount,
      0
    )
  }

  get totalPrice(): number {
    return this.estimate.price.total
  }

  get subtotalPrice(): number {
    return this.extract<number>(this.estimate, 'price.order.priceIncludingTax.price', 0)
  }

  get shippingFee(): number {
    return this.extract<number>(this.estimate, 'price.delivery.priceIncludingTax.price', 0)
  }

  get commission(): number {
    return this.extract<number>(this.estimate, 'price.payment.priceNoTax.price', 0)
  }

  get estimatedDiscountPrice(): number {
    return this.extract<number>(this.estimate, 'price.discount.total.amount', 0)
  }

  get discountPrice(): number {
    return Math.min(this.estimatedDiscountPrice, this.subtotalPrice)
  }

  get discountAmount(): DiscountAmount {
    return new DiscountAmount(
      DiscountPrice.valueOf(this.discountPrice),
      AppliedPoint.valueOf(this.pointDiscounted)
    )
  }

  get serviceFee(): number {
    return this.estimate.price.service_fee.priceIncludingTax.price
  }

  get estimatedPriceSection(): FrontPriceSection {
    return this.estimate.price
  }

  get bundleDiscountPrice(): number {
    return this.estimatedPriceSection.discount ?
      this.estimatedPriceSection.discount.bundle.amount :
      0
  }

  get couponDiscountPrice(): number {
    return this.estimatedPriceSection.discount ?
      this.estimatedPriceSection.discount.coupon.amount :
      0
  }

  get estimatedPoint(): number {
    return this.estimate.point.amount
  }

  get orderIsPreOrder(): boolean {
    return this.order.kind === OrderKind.PreOrder
  }

  get deliveryPlanLabel(): string {
    return this.orderIsPreOrder ?
      `${this.order.deliveryWhen}発送予定` :
      '順次発送予定'
  }

  /**
   * TODO: 実装の重複を解除したい
   * 既存でOrder.vueがstore (displayItemsWithFilter) とisSinglePurchasableを利用してフィルタリングしたもの
   */
  filterDisplayItems(items: CartItem[]): CartItem[] {
    return items.filter(item => filterValidItemAsAggregate(item, items))
      .filter(item => isDisplayItem(item))
      .filter(item => isSinglePurchasable(item))
  }

  /**
   * lodashのgetのエイリアス的なもの
   * @deprecated 型定義を整理して直接アクセスできることが望ましい
   */
  private extract<T>(object: object, path: string, defaultValue: T): T {
    return get(object, path, defaultValue)
  }
}

/**
 * チェックアウトの同一性の識別子
 */
export class CheckoutKey {
  static create(order: Order): CheckoutKey {
    return new CheckoutKey(
      order.cartItems.map(i => i.id).sort().join('_')
    )
  }

  private constructor(
    private readonly value: string
  ) {}

  equalsTo(other: CheckoutKey): boolean {
    return this.value === other.value
  }

  toString(): string {
    return this.value
  }
}

export class MismatchLengthError extends BaseError {}
